import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, User } from 'lucide-react';
import { useEffectiveSerial } from '@/hooks/useEffectiveSerial';

// Dummy data for UI development
const dummyLevels = [
  { _id: '1', name: 'Kid', description: 'Most restricted - suitable for young children' },
  { _id: '2', name: 'Teen', description: 'Moderate restrictions - suitable for teenagers' },
  { _id: '3', name: 'Adult', description: 'Minimal restrictions - full access' },
];

const UserLevel: React.FC = () => {
  const navigate = useNavigate();
  const { serial } = useEffectiveSerial();
  const [selectedLevelId, setSelectedLevelId] = useState('2'); // Default to Teen

  const currentLevel = dummyLevels.find(l => l._id === selectedLevelId);

  const handleLevelChange = (newLevelId: string) => {
    setSelectedLevelId(newLevelId);
    console.log('Level changed to:', newLevelId);
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/${serial}/settings`)}
            className="p-2"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">User Level</h1>
            <p className="text-muted-foreground">Manage your restriction level</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Current Level
            </CardTitle>
            <CardDescription>
              Your level determines the default restrictions applied to your device
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Select Level
              </label>
              <Select
                value={selectedLevelId}
                onValueChange={handleLevelChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a level..." />
                </SelectTrigger>
                <SelectContent>
                  {dummyLevels.map((levelOption) => (
                    <SelectItem key={levelOption._id} value={levelOption._id}>
                      <div className="flex flex-col">
                        <span className="font-medium">{levelOption.name}</span>
                        {levelOption.description && (
                          <span className="text-xs text-muted-foreground">
                            {levelOption.description}
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {currentLevel && (
              <div className="p-4 bg-muted/50 rounded-lg">
                <h3 className="font-medium text-foreground mb-2">Current Level: {currentLevel.name}</h3>
                {currentLevel.description && (
                  <p className="text-sm text-muted-foreground">{currentLevel.description}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UserLevel;
